import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppShell, { CartButton } from './AppShell';
import Sheet from './Sheet';
import Carousel from './Carousel';
import Reveal from './Reveal';
import { readCart, writeCart } from '../hooks/useCart';
import { formatCurrency, publicImg, API_BASE } from '../config/api';
import './GiftBoxPage.css';

const WEIGHT_EXTRA = 10000;
const EGG_EXTRA = 7000;

const BOXES = [
    {
        id: 'single',
        name: 'Mua lẻ 1 bánh',
        short: 'Bánh lẻ (1 cái)',
        capacity: 1,
        img: '/imgs/banh-nuong-truyen-thong.webp',
        desc: 'Đóng gói túi bóng kính giữ ẩm (Không kèm hộp quà)',
    },
    {
        id: 'box2',
        name: 'Hộp 2 Bánh',
        short: 'Hộp 2 bánh',
        capacity: 2,
        img: '/imgs/hop-2-banh.webp',
        desc: 'Tặng kèm vỏ hộp trang nhã & túi xách',
    },
    {
        id: 'box4',
        name: 'Hộp 4 Bánh',
        short: 'Hộp 4 bánh',
        capacity: 4,
        img: '/imgs/hop-4-banh.webp',
        desc: 'Tặng kèm vỏ hộp nghệ thuật + túi xách sen cá chép',
    },
];

const CAKES = [
    { id: 1, name: 'Bánh Nướng Thập Cẩm', kind: 'Bánh nướng', basePrice: 55000, img: '/imgs/nuong-thap-cam.jpg', desc: 'Nhân thập cẩm lạp xưởng, hạt dưa mứt bí giòn bùi.' },
    { id: 3, name: 'Bánh Nướng Đậu Xanh Vani', kind: 'Bánh nướng', basePrice: 45000, img: '/imgs/nuong-dau-xanh-vani.jpeg', desc: 'Đậu xanh nhuyễn mịn rất ít đường, hương vani thơm dịu.' },
    { id: 7, name: 'Bánh Nướng Trà Xanh', kind: 'Bánh nướng', basePrice: 48000, img: '/imgs/nuong-tra-xanh.jpeg', desc: 'Matcha Nhật thơm mát mộc, vị ngọt thanh không gắt.' },
    { id: 5, name: 'Bánh Nướng Cốm Dừa', kind: 'Bánh nướng', basePrice: 48000, img: '/imgs/nuong-com-dua.webp', desc: 'Cốm non mùa thu quyện cơm dừa nạo sợi béo bùi.' },
    { id: 11, name: 'Bánh Dẻo Kem Trứng Muối', kind: 'Bánh dẻo', basePrice: 55000, img: '/imgs/deo-kem-trung-muoi.jpeg', desc: 'Vỏ dẻo hoa bưởi ôm nhân kem trứng muối béo ngậy.' },
    { id: 6, name: 'Bánh Dẻo Thập Cẩm', kind: 'Bánh dẻo', basePrice: 55000, img: '/imgs/deo-thap-cam.jpg', desc: 'Vỏ dẻo thanh mát hòa nhân thập cẩm lạp sườn sần sật.' },
    { id: 10, name: 'Bánh Dẻo Cốm Dừa', kind: 'Bánh dẻo', basePrice: 48000, img: '/imgs/deo-com-dua.jpg', desc: 'Cốm non sữa lúa dẻo thơm cùng sợi dừa tươi non.' },
    { id: 9, name: 'Bánh Dẻo Trà Xanh', kind: 'Bánh dẻo', basePrice: 48000, img: '/imgs/deo-tra-xanh.png', desc: 'Sắc xanh ngọc bích từ bột trà, thanh nhiệt dịu ngọt.' },
    { id: 8, name: 'Bánh Dẻo Đậu Xanh Vani', kind: 'Bánh dẻo', basePrice: 45000, img: '/imgs/deo-dau-xanh-vani.webp', desc: 'Đậu xanh ướp nước hoa bưởi truyền thống nhẹ nhàng.' },
    { id: 12, name: 'Bánh Dẻo Không Nhân', kind: 'Bánh dẻo', basePrice: 20000, img: '/imgs/image-1788536205898-135732376.webp', desc: 'Bánh dẻo chay truyền thống thơm hoa bưởi, ngọt thanh thuần khiết.' },
];

export default function GiftBoxPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const initialBox = BOXES.find((b) => b.id === query.get('box')) || BOXES[1];
    const prePickId = query.get('cakeId');

    const [box, setBox] = useState(initialBox);
    const [cakes, setCakes] = useState(CAKES);
    const [items, setItems] = useState([]);
    const [kindFilter, setKindFilter] = useState('Tất cả');

    // Tải danh sách bánh trực tiếp từ cơ sở dữ liệu để đồng bộ giá và ảnh mới nhất
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/product/getProduct`);
                const list = res.data?.data;
                if (alive && Array.isArray(list) && list.length > 0) {
                    const apiCakes = list
                        .filter(p => !p.name?.includes('Hộp') && p.stock_status !== 'soldout')
                        .map(p => ({
                            id: p.id,
                            name: p.name,
                            kind: (p.category || '').includes('dẻo') ? 'Bánh dẻo' : 'Bánh nướng',
                            basePrice: Number(p.price) || 0,
                            img: p.imageUrl || '/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg',
                            desc: p.description || ''
                        }));
                    if (apiCakes.length > 0) {
                        setCakes(apiCakes);
                    }
                }
            } catch (err) {
                console.warn('Dùng danh sách bánh mặc định:', err.message);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Option sheet
    const [pick, setPick] = useState(null);
    const [weight, setWeight] = useState('150g');
    const [egg, setEgg] = useState(false);

    const listRef = useRef(null);

    const full = items.length >= box.capacity;
    const complete = items.length === box.capacity;
    const subtotal = items.reduce((s, it) => s + it.price, 0);

    const openOptions = (cake) => {
        if (items.length >= box.capacity) {
            Swal.fire({
                icon: 'info',
                title: 'Hộp đã đầy',
                text: `${box.name} chứa tối đa ${box.capacity} bánh. Bạn có thể bỏ bớt một bánh hoặc chuyển sang hộp lớn hơn.`,
                confirmButtonColor: '#142C54',
                confirmButtonText: 'Đã hiểu',
            });
            return;
        }
        setPick(cake);
        setWeight('150g');
        setEgg(false);
    };

    // Deep link from the menu: open that cake's option sheet straight away
    useEffect(() => {
        if (!prePickId) return;
        const cake = cakes.find((c) => String(c.id) === String(prePickId) || c.name?.toLowerCase().includes(String(prePickId).toLowerCase()));
        if (cake) openOptions(cake);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prePickId, cakes]);

    const pickPrice = pick
        ? pick.basePrice + (weight === '180g' ? WEIGHT_EXTRA : 0) + (egg ? EGG_EXTRA : 0)
        : 0;

    const changeBox = (next) => {
        setBox(next);
        if (items.length > next.capacity) setItems(items.slice(0, next.capacity));
    };

    const confirmAdd = () => {
        if (!pick) return;
        setItems((prev) => [...prev, {
            uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            id: pick.id,
            name: pick.name,
            kind: pick.kind,
            img: pick.img,
            weight,
            hasEgg: egg,
            price: pickPrice,
        }]);
        setPick(null);
    };

    const removeItem = (uid) => setItems((prev) => prev.filter((it) => it.uid !== uid));

    const addBoxToCart = () => {
        if (!complete) return;

        const isSingle = box.id === 'single';
        const singleCake = items[0];

        const itemsSummary = isSingle
            ? `${singleCake.name} (${singleCake.weight}, ${singleCake.hasEgg ? '1 trứng muối' : 'không trứng'}) · Không hộp`
            : items.map((c) => `${c.name} (${c.weight}, ${c.hasEgg ? '1 trứng muối' : 'không trứng'})`).join(' · ');

        const cart = readCart();
        cart.push({
            id: `${isSingle ? 'single' : 'box'}-${Date.now()}`,
            name: isSingle ? `Bánh lẻ: ${singleCake.name}` : box.name,
            isBox: !isSingle,
            isSingle,
            capacity: box.capacity,
            boxImage: isSingle ? singleCake.img : box.img,
            imageUrl: isSingle ? singleCake.img : box.img,
            img: isSingle ? singleCake.img : box.img,
            weight: isSingle ? singleCake.weight : undefined,
            hasEgg: isSingle ? singleCake.hasEgg : undefined,
            items,
            itemsSummary,
            price: subtotal,
            qty: 1,
            total: subtotal,
        });
        writeCart(cart);

        Swal.fire({
            icon: 'success',
            title: 'Đã thêm vào giỏ!',
            text: isSingle
                ? `1 bánh ${singleCake.name} (không kèm hộp) đã vào giỏ hàng.`
                : `${box.name} (${items.length} bánh) đã được đóng hộp xong.`,
            showCancelButton: true,
            confirmButtonColor: '#142C54',
            cancelButtonColor: '#4A3A32',
            confirmButtonText: 'Xem giỏ hàng',
            cancelButtonText: isSingle ? 'Chọn thêm bánh' : 'Ghép hộp nữa',
        }).then((res) => {
            if (res.isConfirmed) navigate('/cart');
            else setItems([]);
        });
    };

    const visibleCakes = kindFilter === 'Tất cả' ? cakes : cakes.filter((c) => c.kind === kindFilter);

    return (
        <AppShell
            title={box.id === 'single' ? 'Mua bánh lẻ' : 'Ghép hộp quà'}
            subtitle={`${box.name} · ${items.length}/${box.capacity} bánh`}
            back
            onBack={() => navigate('/menu')}
            right={<CartButton />}
            crumbs={[{ label: 'Menu bánh', to: '/menu' }, { label: box.id === 'single' ? 'Mua bánh lẻ' : 'Ghép hộp quà' }]}
            className="no-desk-bar"
            actionBar={(
                <>
                    <div className="bar-total">
                        <div className="bar-total-label">
                            {complete ? (box.id === 'single' ? 'Giá bánh' : 'Tổng tiền hộp') : `Còn thiếu ${box.capacity - items.length} bánh`}
                        </div>
                        <div className="bar-total-value tick" key={subtotal}>{formatCurrency(subtotal)}</div>
                    </div>
                    <button
                        type="button"
                        className={`btn btn-primary ${complete ? 'ready' : ''}`}
                        disabled={!complete}
                        onClick={addBoxToCart}
                    >
                        <i className="fa-solid fa-basket-shopping" style={{ fontSize: 13 }} />
                        Thêm vào giỏ
                    </button>
                </>
            )}
        >
            {/* Box switcher */}
            <div className="shell">
                <div className="seg box-seg">
                    {BOXES.map((b) => (
                        <button key={b.id} type="button" className={box.id === b.id ? 'on' : ''} onClick={() => changeBox(b)}>
                            <i className={b.id === 'single' ? 'fa-solid fa-cookie-bite' : 'fa-solid fa-gift'} style={{ marginRight: 6, fontSize: 12 }} />
                            {b.short}
                        </button>
                    ))}
                </div>
            </div>

            <div className="shell gift-grid">
                {/* Tray */}
                <div className="gift-side">
                <div className="tray card">
                    <div className="tray-head">
                        <div className="tray-head-left">
                            <img
                                src={publicImg(box.img)}
                                alt={box.name}
                                className="tray-box-img"
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                            />
                            <div>
                                <div className="tray-title">{box.name}</div>
                                {box.id === 'single' ? (
                                    <div className="tray-free" style={{ color: 'var(--muted)', background: 'rgba(0,0,0,0.04)' }}>
                                        <i className="fa-solid fa-box-open" /> Đóng túi giữ ẩm · Không hộp
                                    </div>
                                ) : (
                                    <div className="tray-free"><i className="fa-solid fa-gift" /> Vỏ hộp &amp; túi xách miễn phí</div>
                                )}
                            </div>
                        </div>
                        {items.length > 0 && (
                            <button type="button" className="tray-clear" onClick={() => setItems([])}>Xoá hết</button>
                        )}
                    </div>

                    <div className="tray-progress">
                        <div className="tray-progress-fill" style={{ width: `${(items.length / box.capacity) * 100}%` }} />
                    </div>

                    <div className={`tray-slots cap-${box.capacity}`}>
                        {items.map((it, i) => (
                            <div key={it.uid} className="slot-filled">
                                <button
                                    type="button"
                                    className="slot-remove"
                                    aria-label={`Bỏ ${it.name} khỏi hộp`}
                                    onClick={() => removeItem(it.uid)}
                                >
                                    <i className="fa-solid fa-xmark" />
                                </button>
                                <img
                                    src={publicImg(it.img)}
                                    alt={it.name}
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = publicImg('/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg'); }}
                                />
                                <div className="slot-name clamp-2">{it.name.replace('Bánh ', '')}</div>
                                <div className="slot-tags">
                                    <span>{it.weight}</span>
                                    {it.hasEgg && <span className="egg">Trứng</span>}
                                </div>
                                <div className="slot-price">{formatCurrency(it.price)}</div>
                                <span className="slot-index">{i + 1}</span>
                            </div>
                        ))}

                        {Array.from({ length: box.capacity - items.length }).map((_, i) => (
                            <button
                                key={`empty-${i}`}
                                type="button"
                                className="slot-empty"
                                onClick={() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            >
                                <span className="slot-empty-plus"><i className="fa-solid fa-plus" /></span>
                                <span className="slot-empty-label">Vị trí {items.length + i + 1}</span>
                            </button>
                        ))}
                    </div>

                    {/* Desktop receipt — on phones this lives in the bottom bar */}
                    <div className="tray-receipt desk-only">
                        <div className="tray-line">
                            <span>{items.length} bánh trong hộp</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="tray-line">
                            <span>Vỏ hộp &amp; túi xách</span>
                            <span className="free">Miễn phí</span>
                        </div>
                        <div className="tray-line total">
                            <span>Tổng tiền hộp</span>
                            <strong className="tick" key={subtotal}>{formatCurrency(subtotal)}</strong>
                        </div>

                        <button
                            type="button"
                            className={`btn btn-primary btn-block ${complete ? 'ready' : ''}`}
                            disabled={!complete}
                            onClick={addBoxToCart}
                        >
                            <i className="fa-solid fa-basket-shopping" style={{ fontSize: 13 }} />
                            {complete ? 'Thêm vào giỏ' : `Còn thiếu ${box.capacity - items.length} bánh`}
                        </button>
                    </div>
                </div>
                </div>

                {/* Cake picker */}
                <div className="gift-main" ref={listRef}>
                <div className="section-head">
                    <h2 className="h-section">Chọn vị bánh</h2>
                    <span className="t-muted">{items.length}/{box.capacity} đã xếp</span>
                </div>

                <Carousel className="kind-row" ariaLabel="Lọc dòng bánh">
                    {['Tất cả', 'Bánh nướng', 'Bánh dẻo'].map((k) => (
                        <button key={k} type="button" className={`chip ${kindFilter === k ? 'on' : ''}`} onClick={() => setKindFilter(k)}>
                            {k}
                        </button>
                    ))}
                </Carousel>

                <Reveal stagger className="picker-list" key={kindFilter}>
                    {visibleCakes.map((cake) => {
                        const inBox = items.filter((it) => it.id === cake.id).length;
                        return (
                            <button
                                key={cake.id}
                                type="button"
                                className={`picker-row ${inBox ? 'chosen' : ''}`}
                                onClick={() => openOptions(cake)}
                                disabled={full}
                            >
                                <div className="picker-thumb">
                                    <img
                                        src={publicImg(cake.img)}
                                        alt={cake.name}
                                        loading="lazy"
                                        className="img-fade"
                                        onLoad={(e) => e.currentTarget.classList.add('loaded')}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = publicImg('/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg');
                                            e.currentTarget.classList.add('loaded');
                                        }}
                                    />
                                    {inBox > 0 && <span className="picker-count">{inBox}</span>}
                                </div>

                                <div className="picker-info">
                                    <div className="picker-kind">{cake.kind}</div>
                                    <div className="picker-name">{cake.name}</div>
                                    <div className="picker-desc clamp-2">{cake.desc}</div>
                                </div>

                                <div className="picker-right">
                                    <div className="t-price picker-price">{formatCurrency(cake.basePrice)}</div>
                                    <span className="picker-add"><i className="fa-solid fa-plus" /></span>
                                </div>
                            </button>
                        );
                    })}
                </Reveal>

                <div className="free-note">
                    <i className="fa-solid fa-gift" />
                    <div>
                        <strong>Vỏ hộp {box.capacity === 4 ? '& túi xách sen cá chép' : 'trang nhã'}</strong>
                        <span>Tặng kèm miễn phí khi ghép đủ {box.capacity} bánh — không phụ thu tiền hộp.</span>
                    </div>
                    <span className="free-tag">0 ₫</span>
                </div>
                </div>
            </div>

            {/* Options sheet */}
            <Sheet
                open={!!pick}
                onClose={() => setPick(null)}
                subtitle={pick ? `Vị trí ${items.length + 1} / ${box.capacity}` : ''}
                title={pick?.name}
                footer={(
                    <button type="button" className="btn btn-primary btn-block btn-lg" onClick={confirmAdd}>
                        Cho vào hộp · {formatCurrency(pickPrice)}
                    </button>
                )}
            >
                {pick && (
                    <>
                        <div className="opt-hero">
                            <img
                                src={publicImg(pick.img)}
                                alt={pick.name}
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = publicImg('/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg'); }}
                            />
                            <div>
                                <div className="opt-kind">{pick.kind}</div>
                                <div className="opt-desc">{pick.desc}</div>
                            </div>
                        </div>

                        <div className="sheet-label">1 · Trọng lượng bánh</div>
                        <div className="opt-grid">
                            <button type="button" className={`opt ${weight === '150g' ? 'on' : ''}`} onClick={() => setWeight('150g')}>
                                <span className="opt-name">150g</span>
                                <span className="opt-sub">Chuẩn · vừa khay hộp</span>
                            </button>
                            <button type="button" className={`opt ${weight === '180g' ? 'on' : ''}`} onClick={() => setWeight('180g')}>
                                <span className="opt-name">180g</span>
                                <span className="opt-sub extra">+{formatCurrency(WEIGHT_EXTRA)}</span>
                            </button>
                        </div>

                        {pick.name.includes('Không Nhân') ? (
                            <div className="opt-hint" style={{ marginTop: 14 }}>
                                <i className="fa-solid fa-leaf" style={{ color: '#16A34A', marginRight: 6 }} />
                                Bánh dẻo chay truyền thống không nhân, thanh tịnh thuần khiết (không trứng muối).
                            </div>
                        ) : (
                            <>
                                <div className="sheet-label">2 · Trứng muối</div>
                                <div className="opt-grid">
                                    <button type="button" className={`opt ${!egg ? 'on' : ''}`} onClick={() => setEgg(false)}>
                                        <span className="opt-name">
                                            {pick.name.toLowerCase().includes('kem trứng muối') ? 'Không thêm trứng' : 'Không trứng'}
                                        </span>
                                        <span className="opt-sub">
                                            {pick.name.toLowerCase().includes('kem trứng muối') ? 'Nhân kem trứng dẻo · 0 ₫' : 'Vị thanh mộc · 0 ₫'}
                                        </span>
                                    </button>
                                    <button type="button" className={`opt ${egg ? 'on' : ''}`} onClick={() => setEgg(true)}>
                                        <span className="opt-name">
                                            {pick.name.toLowerCase().includes('kem trứng muối') ? '+1 Trứng muối nướng' : '+1 Trứng muối'}
                                        </span>
                                        <span className="opt-sub extra">+{formatCurrency(EGG_EXTRA)}</span>
                                    </button>
                                </div>
                            </>
                        )}

                        {pick.name.toLowerCase().includes('kem trứng muối') && (
                            <div className="opt-hint">
                                <i className="fa-solid fa-circle-info" /> Bánh dẻo này vốn đã có sẵn nhân kem trứng muối.
                            </div>
                        )}

                        <div className="opt-total">
                            <span>Tạm tính chiếc bánh này</span>
                            <strong>{formatCurrency(pickPrice)}</strong>
                        </div>
                    </>
                )}
            </Sheet>
        </AppShell>
    );
}
